/*
 * Author: Robert Watkins
 * Date: 21/08/2026
 *
 * This file has been added to handle the Cylindo viewer and its shadow roots.
 * It reads the shadow root within the Cylindo viewer and copies the thumbnail
 * HTML into our own thumbnail container so that the thumbnails can be displayed
 * to the left of the viewer on desktop.
 *
 * There are different versions of the functionality depending on the device.
 * On desktop, the thumbnail viewer is positioned to the left of the main
 * Cylindo viewer, while on mobile the layout and sizing are handled differently
 * to make better use of the available screen space.
 *
 * This file also handles additional behaviour required when the Cylindo viewer
 * is displayed in fullscreen mode, including how the viewer and images are
 * positioned and displayed.
 *
 * Image sizing is also managed here. The Cylindo viewer can render images at
 * different heights depending on the selected image, viewer state and device.
 * This file makes the necessary adjustments so that the viewer, thumbnail
 * container and surrounding product media remain correctly sized.
 *
 * The main functionality in this file is:
 *
 * - Read and interact with the Cylindo viewer shadow root.
 * - Read and copy the Cylindo thumbnail buttons into the site thumbnail
 *   container.
 * - Allow the copied thumbnails to control the Cylindo viewer.
 * - Position and size the thumbnail viewer on desktop.
 * - Handle the different layout requirements on mobile.
 * - Monitor the Cylindo viewer as its content changes.
 * - Resize the viewer and surrounding containers based on the rendered image.
 * - Handle the viewer's fullscreen state.
 * - Adjust image and viewer heights where required to prevent layout issues.
 *
 * This functionality has been added because the Cylindo thumbnail viewer is
 * contained within a shadow root, meaning it cannot be directly controlled
 * using the normal page DOM. The code therefore reads the required elements
 * from the shadow root and reproduces the required thumbnail functionality
 * within the site's own layout.
 *
 * TO DO:
 *
 * - Ensure any future changes to the Cylindo viewer or thumbnail structure
 *   are reviewed against this functionality.
 * - Review the desktop and mobile behaviour whenever the viewer integration
 *   is changed.
 * - Retest fullscreen behaviour and image sizing after any changes.
 *
 * When making changes to this file, ensure that all aspects of the features
 * within this file are retested, including desktop, mobile, thumbnail
 * interaction, image sizing and fullscreen behaviour.
 */

neptune.curator = {
  elements: {
    viewer: null,
    scrollBar: null,
    productMediaContainer: null,
    curatorContainer: null,
  },

  shadowroots: {
    viewer: null,
    thumbnail: null,
  },

  interations: [],

  watchingInterval: null,

  setmode: function (index, element) {
    window.viewerBarShadowRoot
      .querySelectorAll(`.cylindo-thumbnail-bar-inner button`)
      [index].click();
    neptune.curator.interations.push(element);
    neptune.curator.updatethumbs();
  },

  init: function () {
    let interval1 = setInterval(() => {
      const viewer_bar = document.querySelector("cylindo-thumbnail-bar");
      var viewer_frame = document.querySelector(
        "cylindo-viewer[remote-config]",
      );

      if (!viewer_bar || !viewer_bar.shadowRoot) {
        return;
      }

      let thumbnailBars = viewer_bar.shadowRoot.querySelectorAll("button");

      if (thumbnailBars.length) {
        clearInterval(interval1);
        neptune.curator.shadowroots.thumbnail = viewer_bar.shadowRoot;
        neptune.curator.shadowroots.viewer = viewer_frame.shadowRoot;

        neptune.curator.elements.viewer = document.querySelector(
          "cylindo-viewer[remote-config]",
        );

        neptune.curator.elements.scrollBar =
          document.querySelector(".js-scroll-bar");

        neptune.curator.elements.productMediaContainer = document.querySelector(
          ".js-product-media-container",
        );

        neptune.curator.elements.curatorContainer =
          document.querySelector(".curator-container");

        window.viewerBarShadowRoot = viewer_bar.shadowRoot;
        window.viewerFrameShadowRoot = viewer_frame.shadowRoot;

        var style = document.createElement("style");

        style.innerHTML =
          window.viewerBarShadowRoot.querySelector("style").innerHTML;

        document.body.appendChild(style);

        neptune.curator.updatethumbs();
        neptune.curator.resize();
        neptune.curator.watching();
      }
    }, 100);
  },

  watching: function () {
    let watching = false;

    neptune.curator.watchingInterval = setInterval(() => {
      if (watching) {
        return;
      }

      watching = true;

      try {
        neptune.curator.updatethumbs();
        neptune.curator.resize();
        neptune.curator.checkFeatureChange();
      } finally {
        watching = false;
      }
    }, 200);
  },

  checkFeatureChange: function () {
    var slotimg = document.querySelector("[remote-config] img[slot]");
    var currentconfig = slotimg.src.split("/products/")[1].split("/")[0];

    if (
      window.neptune.variant.getVariant().default_cylindo_id != currentconfig
    ) {
      console.log("feature changed");
      var features = document.querySelector("cylindo-viewer").features;
      clearInterval(neptune.curator.watchingInterval);
      neptune.curator.watchingInterval = null;

      var newurl = slotimg.src.replace(
        currentconfig,
        window.neptune.variant.getVariant().default_cylindo_id,
      );

      var html = ` <div class="scroll-bar js-scroll-bar"></div>
            <div class="curator-container">
              <cylindo-viewer
                remote-config="${document.querySelector(".curator-viewer").dataset.remoteId}"
              >
                <img
                  slot="placeholder"
                  src="${newurl}"
                />
                <cylindo-thumbnail-bar></cylindo-thumbnail-bar>
              </cylindo-viewer>
            </div>`;

      document.querySelector(".curator-viewer").innerHTML = html;
      neptune.curator.init();
      document.querySelector("cylindo-viewer").features = features;
    }
  },

  updatethumbs: function () {
    var html = ``;

    let thumbnailBars = window.viewerBarShadowRoot?.querySelectorAll("button");

    var index = 0;

    thumbnailBars.forEach((element) => {
      if (element.outerHTML.indexOf("-arrow") == -1) {
        element.setAttribute(
          "onclick",
          "neptune.curator.setmode(" + index + ", this)",
        );

        html += element.outerHTML;
        index++;
      }
    });

    if (neptune.curator.elements.scrollBar.innerHTML != html) {
      neptune.curator.elements.scrollBar.innerHTML = html;
    }
  },

  resize: function () {
    var viewer = neptune.curator.elements.viewer;
    var scrollBar = neptune.curator.elements.scrollBar;
    var productMediaContainer = neptune.curator.elements.productMediaContainer;
    var curatorContainer = neptune.curator.elements.curatorContainer;

    var viewerHeight = viewer.clientHeight;

    scrollBar.setAttribute("style", "max-height:" + viewerHeight + "px");

    var scrollBarWidth = scrollBar.clientWidth;
    var productMediaWidth = productMediaContainer.clientWidth;

    if (window.matchMedia("(min-width:1024px)").matches) {
      curatorContainer.setAttribute(
        "style",
        `width:${productMediaWidth - scrollBarWidth - 20}px;`,
      );
    } else {
      curatorContainer.setAttribute("style", `width:${productMediaWidth}px;`);
    }

    if (document.querySelector("cylindo-viewer[remote-config]").fullscreen) {
      window.viewerFrameShadowRoot.querySelector(
        ".layoutWrapper",
      ).style.display = "flex";
    } else {
      window.viewerFrameShadowRoot.querySelector(
        ".layoutWrapper",
      ).style.display = "block";
    }

    var imageHeight =
      window.viewerFrameShadowRoot.querySelector(
        ".layoutWrapper img",
      ).clientHeight;

    if (window.matchMedia("(max-width:1024px)").matches) {
      if (imageHeight > 0 && neptune.curator.interations.length > 1) {
        window.viewerFrameShadowRoot.querySelector(
          '[part="viewer-content"]',
        ).style.height = imageHeight + "px";
      }
    }

    var thumbnailBarHeight =
      window.viewerBarShadowRoot.querySelector("div").clientHeight;
    viewer.style.height = imageHeight + thumbnailBarHeight * 1.2 + "px";
  },
};
